import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Inbox, RefreshCw, History, FileText, CalendarDays, ListChecks,
  Play, PlayCircle, CheckCircle2, PauseCircle, ClipboardCheck,
  UploadCloud, Trash2, ScanText, Pencil, ArrowRight, CheckCircle, X, Download, Search,
} from "lucide-react";

const fmtDay = (iso) => (iso ? dayjs(iso).format("DD.MM.YYYY") : "—");
const fmtDT = (iso) => (iso ? dayjs(iso).format("DD.MM.YYYY - HH:mm:ss") : "—");
const fmt = (iso) => (iso ? dayjs(iso).format("DD.MM.YYYY · HH:mm:ss") : "—");

// Heft-Kurzname: Bundesland_Blatt_Jahr_Nummer (ohne _start_end und ohne .pdf).
const heftName = (fn = "") => {
  const base = (fn || "").replace(/\.pdf$/i, "");
  const parts = base.split("_");
  return parts.length >= 4 ? parts.slice(0, 4).join("_") : base;
};
const heftKey = (d) => `${d.land || ""}_${d.blatt || ""}`;
const ppTime = (d) => d.pp_at || (["neu", "fertig"].includes(d.status) ? d.created_at : null);
// OK-Zeit = ausschließlich der Zeitpunkt des „Fertig"-Setzens in der PP (kein Fallback auf Bearbeitungszeit).
const okTime = (d) => d.ok_at || null;

// Ampel-Zustand: grün = exportiert, gelb = PP+OK, rot = nur PP, grau = offen.
const ampel = (d) => {
  const hasPP = !!ppTime(d), hasOK = !!okTime(d), exported = !!d.export_at;
  if (hasPP && hasOK && exported) return "gruen";
  if (hasPP && hasOK) return "gelb";
  if (hasPP) return "rot";
  return "grau";
};
const AMPEL_OPTIONS = [
  { k: "alle", label: "Alle Status" },
  { k: "grau", label: "Offen" },
  { k: "rot", label: "Nur PP" },
  { k: "gelb", label: "PP + OK" },
  { k: "gruen", label: "Exportiert" },
];

const STATUS_LABEL = {
  neu: "Neu", fertig: "Fertig", zurueckgestellt: "Zurückgestellt",
  pending: "Ausstehend", processing: "In Analyse", error: "Fehler",
};
const QK_LABEL = { ok: "OK", not_ok: "Not OK", unklar: "Unklar" };

const ACTION = {
  crawler_start: { Icon: Play, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  crawler_start_all: { Icon: PlayCircle, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  finalize: { Icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200" },
  set_qk_status: { Icon: ClipboardCheck, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  zurueckgestellt: { Icon: PauseCircle, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  reanalyze: { Icon: ScanText, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  upload: { Icon: UploadCloud, cls: "bg-gray-100 text-gray-700 border-gray-200" },
  export: { Icon: Download, cls: "bg-[#0F4C81]/10 text-[#0F4C81] border-[#0F4C81]/20" },
  delete: { Icon: Trash2, cls: "bg-red-50 text-red-700 border-red-200" },
  edit: { Icon: Pencil, cls: "bg-gray-100 text-gray-700 border-gray-200" },
  status: { Icon: History, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function ActionBadge({ log }) {
  const a = ACTION[log.action] || ACTION.status;
  const Icon = a.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border ${a.cls}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" /> {log.action_label}
    </span>
  );
}

function Change({ log }) {
  if (log.to_qk || log.from_qk) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        {log.from_qk && <span className="text-gray-400">{QK_LABEL[log.from_qk] || log.from_qk}</span>}
        {log.from_qk && <ArrowRight className="h-3 w-3 text-gray-400" />}
        <span className="font-semibold text-gray-800">{QK_LABEL[log.to_qk] || log.to_qk}</span>
      </span>
    );
  }
  if (log.to_status) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        {log.from_status && <span className="text-gray-400">{STATUS_LABEL[log.from_status] || log.from_status}</span>}
        {log.from_status && <ArrowRight className="h-3 w-3 text-gray-400" />}
        <span className="font-semibold text-gray-800">{STATUS_LABEL[log.to_status] || log.to_status}</span>
      </span>
    );
  }
  return <span className="text-xs text-gray-400">{log.details || "—"}</span>;
}

export default function Protokoll() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(dayjs().format("YYYY-MM-DD")); // Standard: heute
  const [heftFilter, setHeftFilter] = useState("alle");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [query, setQuery] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch { toast.error("Hefte konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try { const r = await api.get("/audit-logs"); setLogs(r.data); }
    catch { toast.error("Protokoll konnte nicht geladen werden"); }
    finally { setLoadingLogs(false); }
  }, []);
  useEffect(() => { if (showLog) loadLogs(); }, [showLog, loadLogs]);

  const heftOptions = useMemo(() => {
    const s = new Set(docs.filter((d) => d.status !== "zurueckgestellt").map(heftKey).filter((k) => k && k !== "_"));
    return Array.from(s).sort();
  }, [docs]);

  // Ein Heft zählt zu einem Tag, wenn IRGENDEINE seiner Zeitangaben (Aufnahme/PP/OK/Export) an dem Tag liegt.
  const matchesDate = useCallback((d, day) => {
    if (!day) return true;
    return [d.created_at, ppTime(d), okTime(d), d.export_at]
      .some((iso) => iso && dayjs(iso).format("YYYY-MM-DD") === day);
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs
      .filter((d) => d.status !== "zurueckgestellt"
        && matchesDate(d, dateFilter)
        && (heftFilter === "alle" || heftKey(d) === heftFilter)
        && (statusFilter === "alle" || ampel(d) === statusFilter)
        && (!q || (d.filename || "").toLowerCase().includes(q) || heftKey(d).toLowerCase().includes(q)))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [docs, dateFilter, heftFilter, statusFilter, query, matchesDate]);

  // Klick auf Ampel-Punkt → Heft im passenden Modus öffnen (fertig → OK, sonst → PP).
  const openHeft = (d) => {
    if (d.status === "fertig") navigate("/ok", { state: { openParent: d.id, ts: Date.now() } });
    else navigate("/hefte", { state: { openDoc: d.id, ts: Date.now() } });
  };

  return (
    <div className="h-full flex flex-col min-h-0 bg-white" data-testid="hefteheute-page">
      {/* Header + Filter */}
      <div className="shrink-0 px-5 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
              <CalendarDays className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-gray-900 text-sm">Hefte Heute</h2>
              <p className="text-[11px] text-gray-400">{rows.length} Hefte · <span className="text-red-500">rot</span> = nur PP · <span className="text-amber-500">gelb</span> = PP+OK · <span className="text-green-600">grün</span> = exportiert</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="hh-search"
                placeholder="Heft suchen (z.B. by_mbl)…"
                className="h-8 text-xs border border-gray-300 rounded-md pl-7 pr-2 w-52 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/30" />
            </div>
            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="hh-date"
              className="h-8 text-xs border border-gray-300 rounded-md px-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/30" />
            <Button size="sm" variant="outline" className={`h-8 text-xs ${dateFilter === dayjs().format("YYYY-MM-DD") ? "border-[#0F4C81] text-[#0F4C81] bg-[#0F4C81]/5" : ""}`} onClick={() => setDateFilter(dayjs().format("YYYY-MM-DD"))} data-testid="hh-today" title="Heutige Hefte anzeigen">Heute</Button>
            <Button size="sm" variant="outline" className={`h-8 text-xs ${!dateFilter ? "border-[#0F4C81] text-[#0F4C81] bg-[#0F4C81]/5" : ""}`} onClick={() => setDateFilter("")} data-testid="hh-all" title="Alle Hefte anzeigen">Alle Hefte</Button>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="hh-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {AMPEL_OPTIONS.map((o) => (
                  <SelectItem key={o.k} value={o.k} data-testid={`hh-filter-status-${o.k}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={heftFilter} onValueChange={setHeftFilter}>
              <SelectTrigger className="h-8 text-xs w-44" data-testid="hh-filter-heft"><SelectValue placeholder="Heft" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle" data-testid="hh-filter-heft-alle">Alle Blätter</SelectItem>
                {heftOptions.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`hh-filter-heft-${k}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowLog((v) => !v)} data-testid="hh-toggle-log" title="Aktivitäts-Protokoll ein-/ausblenden">
              <History className="h-3.5 w-3.5 mr-1" /> {showLog ? "Protokoll ausblenden" : "Protokoll anzeigen"}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={loadDocs} data-testid="hh-refresh" title="Aktualisieren"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Übersichtstabelle */}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading ? (
          <div className="py-20 flex justify-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Inbox className="h-8 w-8 mx-auto" />
            <p className="mt-3 text-sm">Keine Hefte {dateFilter ? `am ${fmtDay(dateFilter)}` : ""}.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="hh-table">
            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 z-10">
              <tr>
                <th className="text-center font-semibold px-4 py-2.5 whitespace-nowrap w-16">Status</th>
                <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">Aufnahmetag</th>
                <th className="text-left font-semibold px-4 py-2.5">Heft</th>
                <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">PP-Zeit</th>
                <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">OK-Zeit</th>
                <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">Export-Zeit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const hasPP = !!ppTime(d);
                const hasOK = !!okTime(d);
                const exported = !!d.export_at;
                // Ampel-Punkt: nur PP → rot, PP+OK → gelb, PP+OK+Export → grün, sonst grau (offen).
                let dotCls = "bg-gray-300";
                let dotTitle = "Offen";
                if (hasPP && hasOK && exported) { dotCls = "bg-green-500"; dotTitle = "Exportiert (grün)"; }
                else if (hasPP && hasOK) { dotCls = "bg-amber-400"; dotTitle = "PP + OK (gelb)"; }
                else if (hasPP) { dotCls = "bg-red-500"; dotTitle = "Nur PP (rot)"; }
                return (
                  <tr key={d.id} data-testid={`hh-row-${d.id}`}
                    className="border-b border-gray-100 align-top hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <button type="button" onClick={() => openHeft(d)} data-testid={`hh-dot-${d.id}`}
                          title={`${dotTitle} — klicken, um das Heft im ${d.status === "fertig" ? "OK" : "PP"}-Modus zu öffnen`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40 cursor-pointer">
                          <span className={`inline-block h-4 w-4 rounded-full shadow-sm ${dotCls}`} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 font-mono">{fmtDay(d.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-gray-900 break-all">{heftName(d.filename)}</span>
                      {exported && <CheckCircle className="inline-block h-3.5 w-3.5 text-green-600 ml-1.5 -mt-0.5" />}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700 font-mono">{fmtDT(ppTime(d))}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700 font-mono">{fmtDT(okTime(d))}</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-xs font-mono ${exported ? "text-green-700 font-semibold" : "text-gray-400"}`}>{fmtDT(d.export_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Ausklappbares Protokoll (Aktions-Log) */}
      {showLog && (
        <div className="shrink-0 h-72 border-t-2 border-gray-200 flex flex-col min-h-0 bg-white" data-testid="hh-log-panel">
          <div className="shrink-0 px-5 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Protokoll · alle Aktionen ({logs.length})</span>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setShowLog(false)} data-testid="hh-log-close" title="Protokoll schließen"><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {loadingLogs ? (
              <div className="py-10 flex justify-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : logs.length === 0 ? (
              <div className="py-10 text-center text-gray-400"><FileText className="h-6 w-6 mx-auto" /><p className="mt-2 text-xs">Noch keine Aktionen.</p></div>
            ) : (
              <table className="w-full text-sm" data-testid="hh-log-table">
                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Datum &amp; Zeit</th>
                    <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Benutzer</th>
                    <th className="text-left font-semibold px-4 py-2">Aktion</th>
                    <th className="text-left font-semibold px-4 py-2">Heft / Blatt</th>
                    <th className="text-left font-semibold px-4 py-2">Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-600 font-mono">{fmt(log.ts)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="text-xs font-semibold text-gray-900">{log.user_name || "—"}</div>
                        <div className="text-[11px] text-gray-400">{log.user_email}</div>
                      </td>
                      <td className="px-4 py-2.5"><ActionBadge log={log} />{log.mode && <span className="ml-1.5 text-[10px] font-bold text-gray-400">{log.mode}</span>}</td>
                      <td className="px-4 py-2.5">
                        {log.filename
                          ? <span className="font-mono text-xs text-gray-800 break-all">{log.filename}</span>
                          : log.blatt
                            ? <span className="text-xs text-gray-700">{(log.blatt || "").toUpperCase()}{log.details ? ` · ${log.details}` : ""}</span>
                            : <span className="text-xs text-gray-400">{log.details || "—"}</span>}
                      </td>
                      <td className="px-4 py-2.5"><Change log={log} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
